(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }
    callback();
  };

  const initFaq = () => {
    document.querySelectorAll('[data-faq-root]').forEach((root) => {
      const searchForm = root.querySelector('[data-fs-faq-search]');
      const searchInput = searchForm?.querySelector('input[type="search"]');
      const categoryButtons = Array.from(root.querySelectorAll('[data-faq-category]'));
      const items = Array.from(root.querySelectorAll('[data-faq-item]'));
      const emptyState = root.querySelector('[data-faq-empty]');
      const resultCount = root.querySelector('[data-faq-count]');
      let activeCategory = 'all';

      const applyFilters = () => {
        const query = searchInput?.value.trim().toLowerCase() || '';
        let visibleCount = 0;

        items.forEach((item) => {
          const matchesCategory = activeCategory === 'all' || item.dataset.faqItemCategory === activeCategory;
          const matchesSearch = !query || (item.dataset.searchText || '').includes(query);
          const isVisible = matchesCategory && matchesSearch;
          item.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        if (emptyState) emptyState.hidden = visibleCount !== 0;
        if (resultCount) resultCount.textContent = `${visibleCount} ${visibleCount === 1 ? 'question' : 'questions'}`;
      };

      categoryButtons.forEach((button) => {
        button.addEventListener('click', () => {
          activeCategory = button.dataset.faqCategory || 'all';
          categoryButtons.forEach((candidate) => {
            candidate.setAttribute('aria-pressed', String(candidate === button));
          });
          applyFilters();
        });
      });

      searchInput?.addEventListener('input', applyFilters);
      searchForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        applyFilters();
      });
      applyFilters();
    });
  };

  const initContactTopic = () => {
    const topicSelect = document.querySelector('[data-contact-topic]');
    if (!topicSelect) return;

    const params = new URLSearchParams(window.location.search);
    const requestedTopic = params.get('topic');
    const partnershipInterest = params.get('interest');
    const interestField = topicSelect.form?.querySelector('[data-contact-interest-field]');
    const interestSelect = interestField?.querySelector('[data-contact-interest]');

    const syncPartnershipInterest = () => {
      if (!interestField || !interestSelect) return;
      const isPartnership = topicSelect.value === 'Partnership inquiry';
      interestField.hidden = !isPartnership;
      interestSelect.required = isPartnership;
      if (!isPartnership) interestSelect.value = '';
    };

    if (requestedTopic) {
      const match = Array.from(topicSelect.options).find(
        (option) => option.value.toLowerCase() === requestedTopic.toLowerCase()
      );
      if (match) topicSelect.value = match.value;
    }

    if (partnershipInterest && interestSelect) {
      const match = Array.from(interestSelect.options).find(
        (option) => option.value.toLowerCase() === partnershipInterest.toLowerCase()
      );
      if (match) interestSelect.value = match.value;
    }

    syncPartnershipInterest();
    topicSelect.addEventListener('change', syncPartnershipInterest);
  };

  const appendText = (parent, tagName, text, className) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    element.textContent = text;
    parent.appendChild(element);
    return element;
  };

  const formatTrackingDate = (value) => {
    if (!value) return 'Update time unavailable';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(date);
    } catch (_) {
      return date.toLocaleString();
    }
  };

  const renderTrackingResult = (container, payload) => {
    const order = payload.order || payload.data || payload;
    const shipments = Array.isArray(order.shipments) ? order.shipments : [];
    container.replaceChildren();

    const heading = document.createElement('div');
    heading.className = 'track-order-result__heading';
    appendText(heading, 'h2', order.name || order.orderName || 'Order found');
    appendText(heading, 'span', order.fulfillmentStatus || order.statusLabel || 'Shipment details');
    container.appendChild(heading);

    if (!shipments.length) {
      appendText(container, 'p', 'This order has not received carrier tracking yet. Please check again after dispatch.');
      return;
    }

    shipments.forEach((shipment) => {
      const shipmentNode = document.createElement('section');
      shipmentNode.className = 'track-order-result__shipment';
      const status = shipment.statusLabel || shipment.status || 'In transit';
      const carrier = shipment.carrier || shipment.carrierName || 'Carrier';
      appendText(shipmentNode, 'h3', status);
      appendText(
        shipmentNode,
        'p',
        [carrier, shipment.trackingNumber].filter(Boolean).join(' | ')
      );

      const events = Array.isArray(shipment.events)
        ? shipment.events
        : Array.isArray(shipment.trackingEvents)
          ? shipment.trackingEvents
          : [];

      if (events.length) {
        const eventList = document.createElement('ol');
        eventList.className = 'track-order-result__events';
        events.forEach((trackingEvent) => {
          const item = document.createElement('li');
          appendText(item, 'time', formatTrackingDate(trackingEvent.occurredAt || trackingEvent.time || trackingEvent.date));
          appendText(item, 'strong', trackingEvent.description || trackingEvent.statusDescription || trackingEvent.status || 'Tracking update');
          const location = trackingEvent.location || trackingEvent.place;
          if (location) appendText(item, 'span', location);
          eventList.appendChild(item);
        });
        shipmentNode.appendChild(eventList);
      } else {
        appendText(shipmentNode, 'p', 'The carrier has not published a detailed scan history yet.');
      }

      container.appendChild(shipmentNode);
    });
  };

  const initTracking = () => {
    document.querySelectorAll('[data-track-order-form]').forEach((form) => {
      const endpoint = (form.dataset.trackingEndpoint || '').trim();
      const submitButton = form.querySelector('[data-track-order-submit]');
      const message = form.querySelector('[data-track-order-message]');
      const panel = form.closest('.track-order-panel');
      const loading = panel?.querySelector('[data-track-order-loading]');
      const result = panel?.querySelector('[data-track-order-result]');

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;

        message?.classList.remove('is-error');
        if (!endpoint) {
          if (message) message.textContent = 'Secure tracking is being connected. Please use My Orders or 17TRACK for now.';
          return;
        }

        const formData = new FormData(form);
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 12000);

        if (submitButton) submitButton.disabled = true;
        if (loading) {
          loading.hidden = false;
          loading.setAttribute('aria-hidden', 'false');
        }
        if (result) result.hidden = true;
        if (message) message.textContent = 'Checking your order and latest carrier updates.';

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              orderNumber: String(formData.get('order_number') || '').trim(),
              email: String(formData.get('email') || '').trim()
            }),
            signal: controller.signal
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok || payload.ok === false) {
            throw new Error(payload.message || 'We could not verify that order. Check both details and try again.');
          }
          if (result) {
            renderTrackingResult(result, payload);
            result.hidden = false;
          }
          if (message) message.textContent = '';
        } catch (error) {
          if (message) {
            message.classList.add('is-error');
            message.textContent = error.name === 'AbortError'
              ? 'Tracking is taking longer than expected. Please try again.'
              : error.message || 'Tracking is temporarily unavailable. Please try again.';
          }
        } finally {
          window.clearTimeout(timeout);
          if (submitButton) submitButton.disabled = false;
          if (loading) {
            loading.hidden = true;
            loading.setAttribute('aria-hidden', 'true');
          }
        }
      });
    });
  };

  ready(() => {
    initFaq();
    initContactTopic();
    initTracking();
  });
})();
